import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import { getPasteboardPlacementBounds } from '@/lib/photos/photo-placement-geometry'
import { rotatedVisualAabbOfShape } from '@/lib/shapes/shape-coordinates'

/** Shared check: does this AABB fall (at least partly) outside the pasteboard's fixed bounds? */
function isAabbOutsidePasteboardBounds(
  aabb: ObjectBounds | null,
  bleedWidth: number,
  bleedHeight: number
): boolean {
  if (!aabb) return false

  const bounds = getPasteboardPlacementBounds(bleedWidth, bleedHeight)

  return (
    aabb.x < bounds.minX ||
    aabb.y < bounds.minY ||
    aabb.x + aabb.width > bounds.maxX ||
    aabb.y + aabb.height > bounds.maxY
  )
}

/**
 * True if any part of the shape's rotation-aware bounding box falls outside the pasteboard's
 * fixed work area (`getPasteboardPlacementBounds` — the same bounds used to clamp photo drops).
 * Any overflow, not just a fully-outside shape, counts: even a partial overflow expands the
 * pasteboard's native scrollable area past its intended fixed extent.
 *
 * Uses the shape's *visual* box (`rotatedVisualAabbOfShape`), not its geometric one — an inset
 * shape's true visual footprint is what the user sees poke past the edge. A geometric-only box (the
 * old `unionCanvasAabbFromShapeObbs`) is the right tool for a group's own resize/rotate gesture math,
 * which must stay geometric, but this is a display/overflow concern with no such constraint, so
 * geometric-only here would both false-flag overflow for a triangle/star whose visual box actually
 * fits, and could miss real overflow for one whose visual corner extends past its geometric one.
 */
export function isShapeOutsidePasteboardBounds(
  shape: ShapeData,
  bleedWidth: number,
  bleedHeight: number
): boolean {
  return isAabbOutsidePasteboardBounds(rotatedVisualAabbOfShape(shape), bleedWidth, bleedHeight)
}
